import { Injectable, inject } from '@angular/core';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { HisBackendRequestOptions, HisBackendService } from '../../../../core/service/backend/his-backend.service';
import { HisUiService } from '../../../../core/service/base/his-ui.service';
import { EudiwMedia } from '../model/eudiw-media.model';
import { EudiwMediaQuery } from '../model/eudiw-media-query.model';
import { EudiwMediaRequest } from '../model/eudiw-media-request.model';
import { LocalMedia } from '../model/local-media.model';
import { LocalMediaRequest } from '../model/local-media-request.model';
import { PropertyCategoryInfo } from '../model/property-category-info.model';
import { ResourcePropertiesRequest } from '../model/resource-properties-request.model';
import { ResourceProperty } from '../model/resource-property.model';
import { ResourcePropertyDetail } from '../model/resource-property-detail.model';

@Injectable({ providedIn: 'root' })
export class HisSelectFileService {
    private backendService = inject(HisBackendService);
    private hisUiService = inject(HisUiService);

    private allMediaCategories$: Observable<Map<string, string>> | undefined;
    private allPropertyCategories$: Observable<Map<string, PropertyCategoryInfo>> | undefined;

    private readonly handledEudiwErrorOptions: HisBackendRequestOptions = {
        showNotFoundError: false,
        showUnexpectedError: false,
    };

    /**
     * Returns a map of all MediaCategory enum constants, mapping each enum key to its i18n message key.
     *
     * The result is cached after the first successful request. If the request fails, the cache is
     * invalidated so that the next call will trigger a new request.
     *
     * @returns an observable of {@code enumKey -> i18nKey} for every MediaCategory constant
     */
    public getAllMediaCategories(): Observable<Map<string, string>> {
        if (!this.allMediaCategories$) {
            this.allMediaCategories$ = this.backendService.getData('api/v1/cs/sys/fileupload/localMedia/allMediaCategories').pipe(
                map((response: Record<string, string>) => new Map(Object.entries(response))),
                catchError((err) => {
                    this.allMediaCategories$ = undefined;
                    return throwError(() => err);
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this.allMediaCategories$;
    }

    /**
     * Returns a map of all PropertyCategory enum constants, mapping each enum key to its i18n
     * message key and the sortorder defining the order in which the categories are presented.
     *
     * The result is cached after the first successful request. If the request fails, the cache is
     * invalidated so that the next call will trigger a new request.
     *
     * @returns an observable of {@code enumKey -> {messageKey, sortorder}} for every PropertyCategory constant
     */
    public getAllPropertyCategories(): Observable<Map<string, PropertyCategoryInfo>> {
        if (!this.allPropertyCategories$) {
            this.allPropertyCategories$ = this.backendService.getData('api/v1/cs/sys/fileupload/resourceProperties/allPropertyCategories').pipe(
                map((response: Record<string, PropertyCategoryInfo>) => new Map(Object.entries(response))),
                catchError((err) => {
                    this.allPropertyCategories$ = undefined;
                    return throwError(() => err);
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this.allPropertyCategories$;
    }

    /**
     * Returns the local media items available for the given upload field configuration
     * and the currently authenticated person.
     *
     * @param request the request containing the configKey and optional context data
     * @returns an observable of available local media items
     */
    public listLocalMedia(request: LocalMediaRequest): Observable<LocalMedia[]> {
        return this.backendService.postData('api/v1/cs/sys/fileupload/localMedia/list', request);
    }

    /** Creates a query that the user can answer with an EUDI wallet. */
    public createEudiwQuery(request: EudiwMediaRequest): Observable<EudiwMediaQuery> {
        return this.backendService.postData(
            'api/v1/cs/sys/fileupload/eudiwMedia/createQuery',
            request,
            this.handledEudiwErrorOptions
        );
    }

    /** Retrieves the media resource supplied for the given EUDI query. */
    public getEudiwMedia(queryId: string): Observable<EudiwMedia> {
        return this.backendService.postData(
            `api/v1/cs/sys/fileupload/eudiwMedia/getResource/${ encodeURIComponent(queryId) }`,
            null,
            this.handledEudiwErrorOptions
        );
    }

    /**
     * Returns the resource properties for the given upload field configuration and file selection
     * as a map of category key -> list of property details (with translated category and property labels).
     *
     * The categories are ordered by the sortorder of the backend PropertyCategory enum, so that they are
     * presented in the same, consistent order as in the JSF component. Categories unknown to the client
     * are appended at the end, keeping the order in which the backend delivered them.
     *
     * @param request the request containing the configKey, optional context data and the selected file
     * @returns an observable of resource property details grouped by category key
     */
    public provideResourceProperties(request: ResourcePropertiesRequest): Observable<Map<string, ResourcePropertyDetail[]>> {
        return forkJoin([
            this.backendService.postData('api/v1/cs/sys/fileupload/resourceProperties/provide', request),
            this.getAllPropertyCategories(),
        ]).pipe(
            map(([properties, categoryInfos]: [ResourceProperty[], Map<string, PropertyCategoryInfo>]) => {
                const categoryMap = properties.reduce((result: Map<string, ResourcePropertyDetail[]>, { category, key, translatedProperty, value, translatedValue }: ResourceProperty) => {
                    const categoryI18nKey = categoryInfos.get(category)?.messageKey || category;
                    const detail: ResourcePropertyDetail = {
                        categoryKey: category,
                        categoryFriendlyName: this.hisUiService.translate(categoryI18nKey) || category,
                        propertyKey: key,
                        propertyKeyFriendlyName: translatedProperty || key,
                        propertyValue: value,
                        propertyValueFriendlyName: translatedValue || value,
                    };
                    if (!result.has(category)) {
                        result.set(category, []);
                    }
                    result.get(category)!.push(detail);
                    return result;
                }, new Map<string, ResourcePropertyDetail[]>());

                return this.sortByCategorySortorder(categoryMap, categoryInfos);
            })
        );
    }

    /**
     * Returns a copy of the given map whose entries are ordered by the sortorder of their category.
     * Categories without a known sortorder are appended at the end in their original order.
     */
    private sortByCategorySortorder(
        categoryMap: Map<string, ResourcePropertyDetail[]>,
        categoryInfos: Map<string, PropertyCategoryInfo>
    ): Map<string, ResourcePropertyDetail[]> {
        const sortedEntries = [...categoryMap.entries()].sort(
            ([categoryA], [categoryB]) =>
                (categoryInfos.get(categoryA)?.sortorder ?? Number.MAX_SAFE_INTEGER)
                - (categoryInfos.get(categoryB)?.sortorder ?? Number.MAX_SAFE_INTEGER)
        );
        return new Map(sortedEntries);
    }
}
